import React, { createContext, useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

const createBulkSelectionStore = () =>
  createStore((set) => ({
    isSelectionMode: false,
    selectedItemUids: new Set(),
    enterSelectionMode: () => {
      set({ isSelectionMode: true, selectedItemUids: new Set() });
    },
    startSelection: (uid) => {
      set({ isSelectionMode: true, selectedItemUids: new Set([uid]) });
    },
    exitSelectionMode: () => {
      set({ isSelectionMode: false, selectedItemUids: new Set() });
    },
    toggleItem: (uid) => {
      set((state) => {
        const selectedItemUids = new Set(state.selectedItemUids);

        if (selectedItemUids.has(uid)) selectedItemUids.delete(uid);
        else selectedItemUids.add(uid);

        return { selectedItemUids };
      });
    },
    toggleAll: (uids) => {
      set((state) => {
        const allSelected = uids.length > 0 && uids.every((uid) => state.selectedItemUids.has(uid));

        return { selectedItemUids: allSelected ? new Set() : new Set(uids) };
      });
    },
  }));

const fallbackStore = createBulkSelectionStore();
const BulkSelectionContext = createContext(fallbackStore);

export const BulkSelectionProvider = ({ children }) => {
  const [store] = useState(createBulkSelectionStore);

  return <BulkSelectionContext.Provider value={store}>{children}</BulkSelectionContext.Provider>;
};

BulkSelectionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBulkSelectionStore = (selector) =>
  useStore(useContext(BulkSelectionContext), selector);

export const useBulkSelectionStoreApi = () => useContext(BulkSelectionContext);

export const useBulkSelectionItem = (uid) => {
  const store = useBulkSelectionStoreApi();
  const selectionState = useStore(store, (state) => {
    if (!state.isSelectionMode) return 'inactive';
    return state.selectedItemUids.has(uid) ? 'selected' : 'unselected';
  });

  const toggleSelection = useCallback(() => {
    store.getState().toggleItem(uid);
  }, [store, uid]);

  const startSelection = useCallback(() => {
    store.getState().startSelection(uid);
  }, [store, uid]);

  return {
    isSelectionMode: selectionState !== 'inactive',
    isSelected: selectionState === 'selected',
    toggleSelection,
    startSelection,
  };
};
